/**
 * Project list component for project creators.
 * Shows all projects created by the user (or all projects if admin).
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { ProjectSummary, ProjectStatus } from '../types';
import { useProjects } from '../hooks/useProjects';
import { useReferenceData } from '../hooks/useReferenceData';
import { LoadingSpinner } from '../../../components/loading/LoadingStates';
import { ErrorDisplay } from '../../../components/errors/ErrorDisplay';

const ProjectList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { projects, loading, error, loadProjects, deleteProject } = useProjects();
  const { referenceData } = useReferenceData();

  // Refresh when returning from create dialog (triggered by query param change)
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.has('refresh')) {
      loadProjects();
      // Clean up URL
      navigate('/projects', { replace: true });
    }
  }, [location.search, loadProjects, navigate]);

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<ProjectSummary | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Filter projects
  const filteredProjects = projects.filter((project) => {
    const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
    const matchesSearch =
      searchQuery === '' ||
      project.projectName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.client.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesStatus && matchesSearch;
  });

  // Get status chip color
  const getStatusColor = (status: ProjectStatus) => {
    switch (status) {
      case ProjectStatus.ACTIVE:
        return 'success';
      case ProjectStatus.COMPLETED:
        return 'default';
      default:
        return 'default';
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Get name from reference data
  const getTherapeuticAreaName = (code: string) => {
    return referenceData?.therapeuticAreas.find(ta => ta.code === code)?.name || code;
  };

  const getIndicationName = (code: string) => {
    return referenceData?.indications.find(ind => ind.code === code)?.name || code;
  };

  // Handle delete project
  const handleDeleteClick = (project: ProjectSummary, event: React.MouseEvent) => {
    event.stopPropagation();
    setProjectToDelete(project);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!projectToDelete) return;

    setDeleting(true);
    try {
      await deleteProject(projectToDelete.projectId);
      setDeleteDialogOpen(false);
      setProjectToDelete(null);
    } catch (err) {
      // Error is already set in the hook
      console.error('Failed to delete project:', err);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setProjectToDelete(null);
  };

  if (loading && projects.length === 0) {
    return <LoadingSpinner message="Loading projects..." />;
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <ErrorDisplay
          title="Failed to load projects"
          error={error}
          onRetry={loadProjects}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h4" sx={{ color: 'white' }}>My Projects</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={loadProjects} disabled={loading} sx={{ color: 'white' }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/projects/create')}
          >
            Create Project
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ bgcolor: 'white', p: 2, borderRadius: 1, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Search"
            placeholder="Search by project name or client..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flexGrow: 1 }}
            size="small"
          />
          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as ProjectStatus | 'all')}
              label="Status"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value={ProjectStatus.ACTIVE}>Active</MenuItem>
              <MenuItem value={ProjectStatus.COMPLETED}>Completed</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Projects Table */}
      {filteredProjects.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {searchQuery || statusFilter !== 'all'
              ? 'No projects match your filters'
              : 'No projects yet'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {searchQuery || statusFilter !== 'all'
              ? 'Try adjusting your filters'
              : 'Create your first project to get started'}
          </Typography>
          {!searchQuery && statusFilter === 'all' && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/projects/create')}
            >
              Create Project
            </Button>
          )}
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Project Name</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Product</TableCell>
                <TableCell>Therapeutic Area</TableCell>
                <TableCell>Indication</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Team Size</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredProjects.map((project) => (
                <TableRow
                  key={project.projectId}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/projects/${project.projectId}`)}
                >
                  <TableCell>
                    <Typography variant="body1" fontWeight="medium">
                      {project.projectName}
                    </Typography>
                  </TableCell>
                  <TableCell>{project.client}</TableCell>
                  <TableCell>{project.product || '—'}</TableCell>
                  <TableCell>{getTherapeuticAreaName(project.therapeuticArea)}</TableCell>
                  <TableCell>{project.indication ? getIndicationName(project.indication) : '—'}</TableCell>
                  <TableCell>
                    <Chip
                      label={project.status}
                      color={getStatusColor(project.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">{project.teamSize}</TableCell>
                  <TableCell>{formatDate(project.projectStart)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/projects/${project.projectId}`);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Project">
                      <IconButton
                        size="small"
                        onClick={(e) => handleDeleteClick(project, e)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Summary */}
      {filteredProjects.length > 0 && (
        <Box sx={{ mt: 2, textAlign: 'right' }}>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Showing {filteredProjects.length} of {projects.length} projects
          </Typography>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Project</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the project "{projectToDelete?.projectName}"?
            <br />
            <br />
            <strong>This will permanently delete:</strong>
            <br />
            • The project and all its details
            <br />
            • All contribution records for this project
            <br />
            <br />
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectList;
